import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { User } from "firebase/auth";
import { useFirebase } from "./FirebaseContext";
import { ApiClient } from "../api/apiClient";
import { useOptionalRolesContext } from "./ModelContext";
import { LicencedProduct } from "../enums/LicencedProduct";

export interface JWTUser {
  hubAdminApiRole?: string;
  CherryHubCherryPayRoles?: string[];
  businessId?: string;
  autoLogOut?: boolean;
}

const isValidJWTUser = (decodedJwt: {
  [key: string]: any;
}): decodedJwt is JWTUser => {
  // A JWT is considered valid if it has a businessId claim
  // Role specific claims are optional. It will be handled in other app code.
  return (
    "businessId" in decodedJwt &&
    (typeof decodedJwt["businessId"] === "string" ||
      typeof decodedJwt["businessId"] === "number")
  );
};

const decodeJwt = (accessToken: string) => {
  try {
    return JSON.parse(window.atob(accessToken.split(".")[1]));
  } catch (e) {
    return {};
  }
};

export type Permission =
  | "Reporting.Financial"
  | "Reporting.Membership"
  | "Cardholder.CreditMoney"
  | "Cardholder.PointsToMoney"
  | "Loyalty.CreditPoints"
  | "Member.InviteToCard"
  | "Member.InviteToApp"
  | "Member.ReadDetails"
  | "Member.Search"
  | "User.Management";

export interface UserContext {
  /** The current logged in user */
  user: User | null;
  /** The role of the user in CherryPay */
  roleIdsByProduct: {
    [product: string]: string[];
  };

  /**
   * The business id which a users access should be scoped to.
   */
  userBusinessId: string | null;

  /** Api client to make authenticated api calls */
  apiClient: ApiClient | null;

  /** Log out of the current session */
  logout: () => Promise<void>;
  /** Is the user context still initialising? */
  isLoading: boolean;
  isSuperAdmin: boolean;
}

export const UserContext = createContext<UserContext | null>(null);

interface UserContextProviderProps {
  children: React.ReactNode;
}

const useDecodedJwt = (user: User | null) => {
  return useMemo<JWTUser | null>(() => {
    if (!user) {
      return null;
    }
    const decoded = decodeJwt(user.stsTokenManager.accessToken);

    if (isValidJWTUser(decoded)) {
      return decoded as JWTUser;
    } else {
      console.error("Decoded JWT is not valid: ", decoded);
      return null;
    }
  }, [user]);
};

export const HUB_ADMIN_ROLE_SUPER_ADMIN = "SuperAdmin";
export const CHERRY_PAY_ROLE_SUPER_USER = "Super User";

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { auth } = useFirebase();

  let userSessionTimeout: any = null;

  useEffect(() => {
    auth.onAuthStateChanged(async (user) => {
      var token = await user?.getIdToken();
      setUser(user);
      setIsLoading(false);

      if (!user) {
        if (userSessionTimeout) {
          clearTimeout(userSessionTimeout);
          userSessionTimeout = null;
        }
        return;
      }

      if (!token) return;

      const decoded = decodeJwt(token);

      if (!decoded || decoded.autoLogOut === false) return;

      const idTokenResult = await user.getIdTokenResult();
      const authTime = Number(idTokenResult.claims.auth_time) * 1000;
      const sessionDurationInMilliseconds = 8 * 60 * 60 * 1000; // 8 hours
      const expirationInMilliseconds =
        sessionDurationInMilliseconds - (Date.now() - authTime);
      userSessionTimeout = setTimeout(
        () => auth.signOut(),
        expirationInMilliseconds
      );
    });
  }, [auth, setUser, setIsLoading, useDecodedJwt]);

  const apiClient = useMemo(() => (user ? new ApiClient(user) : null), [user]);

  // Decode the JWT so that we can access the user claims and determine their role
  const userJwt = useDecodedJwt(user);

  const isSuperAdmin = userJwt?.hubAdminApiRole === HUB_ADMIN_ROLE_SUPER_ADMIN;

  const ctx = useMemo<UserContext>(
    () => ({
      user,
      apiClient,
      isLoading,
      logout: () => auth.signOut(),
      userBusinessId: userJwt?.businessId ?? null,
      isSuperAdmin,
      roleIdsByProduct: {
        CherryPay: userJwt?.CherryHubCherryPayRoles ?? [],
      },
    }),
    [isLoading, auth, apiClient, user, userJwt, isSuperAdmin]
  );

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContext => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("Missing user context");
  }

  return ctx;
};

/**
 * Combines `isSuperAdmin` (from the JWT, always available) with the
 * CherryPay "Super User" role check (which depends on roles loaded by
 * `ModelContextProvider`). Returns `false` until roles are available.
 *
 * Must be called from within `ModelContextProvider` for the role check to
 * take effect; outside it, only `isSuperAdmin` contributes.
 */
export const useIsSuperAdminOrSuperUser = (): boolean => {
  const { isSuperAdmin } = useUserContext();
  const rolesSummary = useOptionalRolesContext();

  const isCherryPaySuperUser = (rolesSummary?.Roles ?? []).some(
    (role) =>
      role.DisplayName === CHERRY_PAY_ROLE_SUPER_USER &&
      role.Product === LicencedProduct.CherryPay
  );

  return isSuperAdmin || isCherryPaySuperUser;
};
